/**
 * Vessel-Surface Fixture page — dev-only.
 *
 * Cycle 109 v2 Sprint 1 (S1.7). Verifies that `.vessel-surface` rebinds the 7
 * nautical-chart tokens (--fg, --fg-secondary, --fg-muted, --fg-dim, --border,
 * --border-strong, --bg-panel) regardless of `html.theme-*` (FR-1.5 / AC-1.7.a).
 *
 * Mounted at `/_fixtures/vessel-surface`. Playwright captures snapshots in
 * both themes as the baseline (visual regression catches future drift).
 */
import { NotchedPanel } from '@/components/common/primitives';
import { TabHeader } from '@/components/common/TabHeader';

export function VesselSurfaceFixturePage() {
  return (
    <div
      data-testid="vessel-surface-fixture"
      style={{
        padding: 24,
        minHeight: '100vh',
        background: 'var(--bg)',
        color: 'var(--fg)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <h1 style={{ marginTop: 0, fontSize: 14, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-muted)' }}>
        Vessel-Surface Fixture
      </h1>
      <p style={{ maxWidth: 600, color: 'var(--fg-secondary)', fontSize: 13, lineHeight: 1.5 }}>
        Outside the .vessel-surface scope, foreground and panel tokens follow the active theme.
        Inside, the 7 nautical-chart tokens rebind to dark high-contrast values — even if the app
        is in light mode. Toggle the theme via the NavRail sun/moon to verify the chart stays dark.
      </p>

      {/* Reference: ambient theme tokens */}
      <section
        data-testid="vessel-ambient"
        style={{
          marginTop: 24,
          padding: 16,
          background: 'var(--bg-panel)',
          color: 'var(--fg)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
        }}
      >
        <p style={{ margin: 0, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
          Ambient panel · color=var(--fg) · background=var(--bg-panel)
        </p>
      </section>

      {/* Vessel-surface scope: tokens rebind to chart values */}
      <section
        data-testid="vessel-scope"
        className="vessel-surface"
        style={{
          marginTop: 24,
          padding: 16,
          background: 'var(--bg-panel)',
          color: 'var(--fg)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
        }}
      >
        <p style={{ margin: 0, marginBottom: 12, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
          Vessel scope · color=var(--fg) · background=var(--bg-panel)
        </p>
        <div
          data-testid="vessel-fg-secondary"
          style={{ fontSize: 12, color: 'var(--fg-secondary)' }}
        >
          Secondary foreground (var(--fg-secondary))
        </div>
        <div
          data-testid="vessel-fg-muted"
          style={{ fontSize: 12, color: 'var(--fg-muted)' }}
        >
          Muted foreground (var(--fg-muted))
        </div>
        <div
          data-testid="vessel-fg-dim"
          style={{ fontSize: 12, color: 'var(--fg-dim)' }}
        >
          Dim foreground (var(--fg-dim))
        </div>
        <hr
          data-testid="vessel-border-strong"
          style={{ marginTop: 12, marginBottom: 12, height: 1, border: 0, background: 'var(--border-strong)' }}
        />
        <div style={{ position: 'relative', height: 120, marginTop: 12 }}>
          <NotchedPanel corners={['tr', 'bl']} bg="var(--bg-panel)">
            <TabHeader label="Vessel panel" variant="standard" right="LIVE" />
            <div style={{ paddingTop: 44, padding: 16, color: 'var(--fg)', fontSize: 12 }}>
              TabHeader + NotchedPanel composed inside the chart surface.
            </div>
          </NotchedPanel>
        </div>
      </section>
    </div>
  );
}

export default VesselSurfaceFixturePage;
